import { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperclip,
  faPaperPlane,
  faTimes,
  faSpinner,
  faCheckCircle,
  faCamera,
  faBug,
  faLightbulb,
  faStar,
  faBolt,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import { useToast } from './Toast';
import { toPng } from 'html-to-image';
import { useFeedback } from '../contexts/FeedbackContext';

const TICKET_CATEGORIES = [
  {
    value: 'Bug Report',
    label: 'Bug Report',
    icon: faBug,
    description: 'Something is not working correctly'
  },
  {
    value: 'Suggestion',
    label: 'Suggestion',
    icon: faLightbulb,
    description: 'An idea to improve the product'
  },
  {
    value: 'Feature Request',
    label: 'Feature Request',
    icon: faStar,
    description: 'Request a new feature'
  },
  {
    value: 'Performance Issue',
    label: 'Performance Issue',
    icon: faBolt,
    description: 'Slow loading or lag'
  },
  { value: 'Other', label: 'Other', icon: faFileAlt, description: 'General inquiry or other' }
];

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

interface FeedbackWidgetProps {
  playableId?: number;
  userId?: number;
  userEmail?: string;
}

export default function FeedbackWidget({ playableId, userId, userEmail }: FeedbackWidgetProps) {
  const { isOpen, closeFeedback, notifySubmitSuccess } = useFeedback();
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('Bug Report');
  const [screenshot, setScreenshot] = useState<{ file: File; preview: string } | null>(null);
  const [attachment, setAttachment] = useState<{ file: File; preview: string | null } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  const [submittedTicket, setSubmittedTicket] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  // Screenshot capture using html-to-image
  const handleCapture = async () => {
    setIsCapturing(true);
    let originalPosition = '';
    let tempImg: HTMLImageElement | null = null;
    let iframe: HTMLIFrameElement | null = null;
    try {
      iframe = document.querySelector('iframe');
      if (iframe && iframe.contentWindow) {
        // Request bridge capture
        const bridgeImgData = await new Promise<{ data: string } | null>(resolve => {
          let resolved = false;

          // eslint-disable-next-line prefer-const
          let timeoutId: NodeJS.Timeout;

          const handler = (event: MessageEvent) => {
            if (resolved) return;
            if (event.data?.type === 'from_game') {
              try {
                const payload =
                  typeof event.data.data === 'string'
                    ? JSON.parse(event.data.data)
                    : event.data.data;
                if (payload.type === 'screenshot') {
                  resolved = true;
                  clearTimeout(timeoutId);
                  window.removeEventListener('message', handler);
                  resolve({ data: payload.data });
                } else if (payload.type === 'screenshot_failed') {
                  resolved = true;
                  clearTimeout(timeoutId);
                  window.removeEventListener('message', handler);
                  resolve(null);
                }
              } catch (_e) {
                // ignore parsing errors
              }
            }
          };

          window.addEventListener('message', handler);
          iframe!.contentWindow!.postMessage(
            { type: 'to_game', data: JSON.stringify({ type: 'capture_screenshot' }) },
            '*'
          );

          timeoutId = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              window.removeEventListener('message', handler);
              resolve(null);
            }
          }, 1500);
        });

        if (bridgeImgData && bridgeImgData.data) {
          tempImg = document.createElement('img');
          tempImg.src = bridgeImgData.data;
          tempImg.style.position = 'absolute';
          tempImg.style.top = '0';
          tempImg.style.left = '0';
          tempImg.style.width = '100%';
          tempImg.style.height = '100%';
          tempImg.style.objectFit = 'contain';
          tempImg.style.zIndex = '9999';
          tempImg.style.pointerEvents = 'none';

          if (iframe.parentElement) {
            const computedStyle = window.getComputedStyle(iframe.parentElement);
            if (computedStyle.position === 'static') {
              originalPosition = iframe.parentElement.style.position || 'none';
              iframe.parentElement.style.position = 'relative';
            }
            iframe.parentElement.appendChild(tempImg);
          }
        }
      }

      const dataUrl = await toPng(document.body, {
        cacheBust: true,
        skipAutoScale: true,
        filter: (node: HTMLElement) => {
          // Exclude the feedback widget from the screenshot
          if (node.id === 'feedback-widget-container') return false;
          // Exclude iframes to prevent html-to-image from crashing due to CORS
          if (node.tagName === 'IFRAME') return false;
          return true;
        }
      });

      // Cleanup overlay image
      if (tempImg && tempImg.parentElement) {
        tempImg.parentElement.removeChild(tempImg);
        if (iframe && iframe.parentElement && originalPosition !== '') {
          if (originalPosition === 'none') {
            iframe.parentElement.style.removeProperty('position');
          } else {
            iframe.parentElement.style.position = originalPosition;
          }
        }
      }

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      const file = new File([blob], 'screenshot.png', { type: 'image/png' });
      const preview = URL.createObjectURL(blob);
      setScreenshot({ file, preview });
    } catch (error) {
      if (tempImg && tempImg.parentElement) {
        tempImg.parentElement.removeChild(tempImg);
        if (iframe && iframe.parentElement && originalPosition !== '') {
          if (originalPosition === 'none') {
            iframe.parentElement.style.removeProperty('position');
          } else {
            iframe.parentElement.style.position = originalPosition;
          }
        }
      }
      console.error('Error capturing screenshot:', error);
      showToast('Failed to capture screenshot', 'error');
    } finally {
      setIsCapturing(false);
    }
  };

  const handleRemoveScreenshot = () => {
    if (screenshot?.preview) {
      URL.revokeObjectURL(screenshot.preview);
    }
    setScreenshot(null);
  };

  // File attachment
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      showToast('File size exceeds 20MB limit', 'error');
      return;
    }

    // Create preview only for images
    let preview: string | null = null;
    if (file.type.startsWith('image/')) {
      preview = URL.createObjectURL(file);
    }

    setAttachment({ file, preview });
  };

  const handleRemoveAttachment = () => {
    if (attachment?.preview) {
      URL.revokeObjectURL(attachment.preview);
    }
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    if (!message.trim() && !screenshot && !attachment) {
      showToast('Please enter a message or attach a file', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      let screenshotPath = null;
      let attachedFilePath = null;

      // Upload screenshot if exists
      if (screenshot) {
        const formData = new FormData();
        formData.append('file', screenshot.file);

        const uploadRes = await fetch('/api/feedback/upload-screenshot', {
          method: 'POST',
          body: formData
        });

        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          screenshotPath = uploadData.path;
        } else {
          throw new Error(uploadData.error || 'Screenshot upload failed');
        }
      }

      // Upload attachment if exists
      if (attachment) {
        const formData = new FormData();
        formData.append('file', attachment.file);

        const uploadRes = await fetch('/api/feedback/upload-file', {
          method: 'POST',
          body: formData
        });

        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          attachedFilePath = uploadData.path;
        } else {
          throw new Error(uploadData.error || 'File upload failed');
        }
      }

      // Submit support ticket
      const response = await fetch('/api/feedback/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          playableId,
          userId,
          userEmail,
          message,
          category,
          screenshotPath,
          attachedFilePath
        })
      });

      const data = await response.json();
      if (data.success) {
        setSubmittedTicket(data.ticketNumber);
        showToast(`Ticket ${data.ticketNumber} created successfully!`, 'success');
        notifySubmitSuccess();
        // Reset form after showing success
        setTimeout(() => {
          setMessage('');
          setCategory('Bug Report');
          handleRemoveScreenshot();
          handleRemoveAttachment();
          setSubmittedTicket(null);
          closeFeedback();
        }, 3000);
      } else {
        showToast(data.error || 'Failed to submit ticket', 'error');
      }
    } catch (error) {
      console.error(error);
      showToast('Error submitting support ticket', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    closeFeedback();
    setSubmittedTicket(null);
  };

  // Only render the popup when open
  if (!isOpen) return null;

  return (
    <>
      {/* Popup Form */}
      {isOpen && (
        <div
          id="feedback-widget-container"
          className="fixed bottom-6 right-6 w-96 rounded-lg shadow-xl border z-50 flex flex-col overflow-hidden animate-fade-in-up"
          style={{
            background: 'var(--bg-card)',
            borderColor: 'var(--border)'
          }}
        >
          <div
            className="p-4 border-b flex justify-between items-center"
            style={{
              background: 'var(--bg-secondary)',
              borderColor: 'var(--border)'
            }}
          >
            <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
              Submit Support Ticket
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {submittedTicket ? (
            <div className="p-8 text-center">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-5xl mb-4"
                style={{ color: 'var(--success)' }}
              />
              <h4 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Ticket Created!
              </h4>
              <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                Your ticket number is:
              </p>
              <code
                className="block text-lg font-mono px-4 py-2 rounded-lg"
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--accent)'
                }}
              >
                {submittedTicket}
              </code>
              <p className="text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
                You can track your ticket in the Support section of your dashboard.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Category Select */}
              <div>
                <label
                  className="block text-sm font-medium mb-1.5"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Category
                </label>
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  className="w-full p-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-all"
                  style={{
                    background: 'var(--bg-input)',
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)'
                  }}
                >
                  {TICKET_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label
                  className="block text-sm font-medium mb-1.5"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Description
                </label>
                <textarea
                  className="w-full p-3 border rounded-lg resize-none text-sm focus:outline-none focus:ring-2 transition-all"
                  style={{
                    background: 'var(--bg-input)',
                    borderColor: 'var(--border)',
                    color: 'var(--text-primary)'
                  }}
                  rows={4}
                  placeholder="Describe your issue or suggestion in detail..."
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                />
              </div>

              {/* Screenshot Preview */}
              {screenshot && (
                <div
                  className="relative border rounded-lg overflow-hidden"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <div
                    className="flex items-center gap-2 p-2 border-b"
                    style={{ borderColor: 'var(--border)', background: 'var(--bg-secondary)' }}
                  >
                    <FontAwesomeIcon
                      icon={faCamera}
                      className="text-xs"
                      style={{ color: 'var(--text-muted)' }}
                    />
                    <span
                      className="text-xs font-medium"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      Screenshot
                    </span>
                  </div>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={screenshot.preview}
                    alt="Screenshot"
                    className="w-full h-24 object-cover"
                  />
                  <button
                    onClick={handleRemoveScreenshot}
                    className="absolute top-2 right-2 w-6 h-6 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-xs transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}

              {/* Attachment Preview */}
              {attachment && (
                <div
                  className="relative border rounded-lg overflow-hidden p-3"
                  style={{
                    borderColor: 'var(--border)',
                    background: 'var(--bg-secondary)'
                  }}
                >
                  <div className="flex items-center gap-3">
                    {attachment.preview ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={attachment.preview}
                        alt="Attachment"
                        className="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <div
                        className="w-12 h-12 flex items-center justify-center rounded"
                        style={{ background: 'var(--bg-primary)' }}
                      >
                        <FontAwesomeIcon
                          icon={faPaperclip}
                          style={{ color: 'var(--text-muted)' }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm truncate font-medium"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {attachment.file.name}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {formatFileSize(attachment.file.size)}
                      </p>
                    </div>
                    <button
                      onClick={handleRemoveAttachment}
                      className="text-gray-400 hover:text-red-400 transition-colors p-1"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,application/pdf,.doc,.docx,.txt,.zip,.json"
              />

              {/* Action Buttons Row */}
              <div className="flex flex-wrap gap-2 pt-2">
                {/* Attach Screen Button */}
                <button
                  onClick={handleCapture}
                  disabled={isCapturing || isSubmitting || !!screenshot}
                  className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors ${
                    screenshot ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/5'
                  }`}
                  style={{
                    color: 'var(--text-secondary)',
                    borderColor: 'var(--border)'
                  }}
                >
                  {isCapturing ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faCamera} />
                  )}
                  <span>Attach Screen</span>
                </button>

                {/* Attach File Button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting || !!attachment}
                  className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors ${
                    attachment ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/5'
                  }`}
                  style={{
                    color: 'var(--text-secondary)',
                    borderColor: 'var(--border)'
                  }}
                >
                  <FontAwesomeIcon icon={faPaperclip} />
                  <span>Attach File</span>
                </button>

                {/* Spacer */}
                <div className="flex-1" />

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || (!message && !screenshot && !attachment)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-2 transition-all duration-200
                              ${
                                isSubmitting || (!message && !screenshot && !attachment)
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:opacity-90 hover:glow'
                              }`}
                  style={{
                    background: 'var(--gradient-primary)'
                  }}
                >
                  {isSubmitting ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faPaperPlane} />
                  )}
                  <span>Submit</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
